export default function Pip() {
    return (
        <ImprovementsDashboard />
    )
}

import { useEffect, useState, useContext } from "react";
import firebase from "firebase/app";
import "firebase/auth";
import "firebase/firestore";
import { Collections } from "../types/collection";
import { Question } from "../interfaces/question.interface";
import { UserContext } from "../contexts/user.context";
import { motion } from "framer-motion";
import { FaCalendarAlt, FaChartBar, FaSearch, FaSort, FaArrowUp, FaTrophy } from "react-icons/fa";
import { formatDistanceToNow, format } from "date-fns";

// Component for the Improvements Dashboard
function ImprovementsDashboard() {
    const { user } = useContext(UserContext);
    const [improvements, setImprovements] = useState<ImprovementItem[]>([]);
    const [sessions, setSessions] = useState<SessionDetail[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [sortBy, setSortBy] = useState<"date" | "score">("date");
    const [skillProgress, setSkillProgress] = useState<SkillProgressData[]>([]);
    const [timelineData, setTimelineData] = useState<TimelinePoint[]>([]);

    useEffect(() => {
        async function fetchData() {
            if (!firebase.auth().currentUser) return;

            setLoading(true);
            try {
                const userSession = await firebase
                    .app()
                    .firestore()
                    .collection(Collections.Sessions)
                    .doc(firebase.auth().currentUser?.uid)
                    .collection('list')
                    .get();

                if (userSession.empty) {
                    setLoading(false);
                    return;
                }

                const sessionDocs = userSession.docs;
                const sessionPromises = sessionDocs.map(async (sessionDoc) => {
                    const sessionId = sessionDoc.id;
                    const sessionData = sessionDoc.data();

                    // Get all runs for this session
                    const runData = await firebase
                        .app()
                        .firestore()
                        .collection(Collections.Runs)
                        .doc(sessionId)
                        .collection('list')
                        .get();

                    const sessionDetail: SessionDetail = {
                        id: sessionId,
                        title: sessionData.title || "Untitled Session",
                        date: sessionData.startedAt?.toDate() || new Date(),
                        interviewType: sessionData.interviewType || "",
                        score: 0,
                        questions: [],
                        improvements: [],
                        skillScores: {
                            behavioral: 0,
                            problemSolving: 0,
                            technicalDepth: 0,
                            communication: 0
                        }
                    };

                    if (!runData.empty) {
                        runData.docs.forEach(doc => {
                            const runQuestionsAnswered = doc.data().questions as Question[];
                            sessionDetail.questions = sessionDetail.questions.concat(runQuestionsAnswered);

                            if (doc.data().overallScore) {
                                sessionDetail.score = doc.data().overallScore;
                            }

                            // Extract skill scores
                            runQuestionsAnswered.forEach(question => {
                                if (question.aiFeedback?.score) {
                                    const scores = question.aiFeedback.score;
                                    sessionDetail.skillScores.behavioral += scores.behavioral || 0;
                                    sessionDetail.skillScores.problemSolving += scores.problemSolving || 0;
                                    sessionDetail.skillScores.technicalDepth += scores.technicalDepth || 0;
                                    sessionDetail.skillScores.communication += scores.communication || 0;
                                }
                            });
                        });

                        // Normalize skill scores if questions exist
                        if (sessionDetail.questions.length > 0) {
                            const questionCount = sessionDetail.questions.length;
                            sessionDetail.skillScores.behavioral /= questionCount;
                            sessionDetail.skillScores.problemSolving /= questionCount;
                            sessionDetail.skillScores.technicalDepth /= questionCount;
                            sessionDetail.skillScores.communication /= questionCount;
                        }

                        const sessionImprovements: string[] = [];
                        sessionDetail.questions.forEach(question => {
                            const aiFeedback = question?.aiFeedback?.areaOfImprovement;
                            if (aiFeedback && aiFeedback.length > 0) {
                                sessionImprovements.push(...aiFeedback);
                            }
                        });

                        sessionDetail.improvements = sessionImprovements;
                    }

                    return sessionDetail;
                });

                const sessionDetails = await Promise.all(sessionPromises);

                // Sort sessions by date for timeline analysis
                const chronologicalSessions = [...sessionDetails].sort((a, b) =>
                    a.date.getTime() - b.date.getTime()
                );

                // Generate timeline data points
                const timeline: TimelinePoint[] = chronologicalSessions.map(session => ({
                    date: session.date,
                    score: session.score,
                    title: session.title,
                    interviewType: session.interviewType
                }));

                // Generate skill progress data
                const skills: SkillProgressData[] = [
                    { name: "Behavioral", data: [] },
                    { name: "Problem Solving", data: [] },
                    { name: "Technical Depth", data: [] },
                    { name: "Communication", data: [] }
                ];

                chronologicalSessions.forEach(session => {
                    skills[0].data.push({
                        date: session.date,
                        value: session.skillScores.behavioral
                    });
                    skills[1].data.push({
                        date: session.date,
                        value: session.skillScores.problemSolving
                    });
                    skills[2].data.push({
                        date: session.date,
                        value: session.skillScores.technicalDepth
                    });
                    skills[3].data.push({
                        date: session.date,
                        value: session.skillScores.communication
                    });
                });

                setTimelineData(timeline);
                setSkillProgress(skills);
                setSessions(sessionDetails);

                // Aggregate all improvements
                const allImprovements: ImprovementItem[] = [];
                sessionDetails.forEach(session => {
                    session.improvements.forEach(improvement => {
                        const existingIndex = allImprovements.findIndex(i => i.text === improvement);
                        if (existingIndex >= 0) {
                            allImprovements[existingIndex].count++;
                            if (!allImprovements[existingIndex].sessions.includes(session.id)) {
                                allImprovements[existingIndex].sessions.push(session.id);
                            }
                        } else {
                            allImprovements.push({
                                text: improvement,
                                count: 1,
                                sessions: [session.id]
                            });
                        }
                    });
                });

                // Sort improvements by frequency
                allImprovements.sort((a, b) => b.count - a.count);
                setImprovements(allImprovements);

            } catch (error) {
                console.error("Error fetching data:", error);
            } finally {
                setLoading(false);
            }
        }

        fetchData();
    }, [user]);

    // Filter sessions based on search term
    const filteredSessions = sessions.filter(session =>
        session.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        session.interviewType.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Sort sessions based on sort criteria
    const sortedSessions = [...filteredSessions].sort((a, b) => {
        if (sortBy === "date") {
            return b.date.getTime() - a.date.getTime();
        } else {
            return b.score - a.score;
        }
    });

    // Calculate top 3 most common improvement areas
    const topImprovements = improvements.slice(0, 3);

    // Calculate progress for each skill
    const calculateSkillProgress = (skillData: { date: Date, value: number }[]) => {
        if (skillData.length < 2) return { value: 0, improved: false };

        const firstValue = skillData[0].value;
        const lastValue = skillData[skillData.length - 1].value;
        const change = lastValue - firstValue;

        return {
            value: Math.abs(change),
            improved: change > 0
        };
    };

    // Find skill with most improvement
    const getMostImprovedSkill = () => {
        if (skillProgress.length === 0) return null;

        let highestImprovement = -1;
        let mostImprovedSkill = "";

        skillProgress.forEach(skill => {
            const progress = calculateSkillProgress(skill.data);
            if (progress.improved && progress.value > highestImprovement) {
                highestImprovement = progress.value;
                mostImprovedSkill = skill.name;
            }
        });

        return mostImprovedSkill || null;
    };

    const mostImprovedSkill = getMostImprovedSkill();

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-8">Your Improvement Dashboard</h1>

            {sessions.length === 0 ? (
                <div className="bg-white rounded-xl shadow p-8 text-center">
                    <h2 className="text-xl text-gray-700 mb-4">No Session Data Available</h2>
                    <p className="text-gray-500">Complete an interview session to see your improvement areas</p>
                </div>
            ) : (
                <>
                    {/* Progress Over Time Section */}
                    {sessions.length > 1 && (
                        <section className="mb-10">
                            <h2 className="text-xl font-semibold text-gray-700 mb-4">Your Progress Over Time</h2>

                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                                {/* Overall Score Card */}
                                <motion.div
                                    className="bg-white rounded-xl shadow p-6"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <h3 className="font-medium text-gray-700 mb-2">Overall Performance</h3>
                                    <div className="flex items-center">
                                        <div className="text-3xl font-bold text-blue-600 mr-2">
                                            {timelineData.length > 0 ?
                                                timelineData[timelineData.length - 1].score : 0}%
                                        </div>
                                        {timelineData.length > 1 && (
                                            <span className={`text-sm font-medium ${timelineData[timelineData.length - 1].score > timelineData[0].score
                                                    ? 'text-green-500' : 'text-red-500'
                                                }`}>
                                                {timelineData[timelineData.length - 1].score > timelineData[0].score
                                                    ? '↑' : '↓'}
                                                {Math.abs(
                                                    timelineData[timelineData.length - 1].score - timelineData[0].score
                                                )}%
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-sm text-gray-500 mt-2">
                                        From your first to most recent session
                                    </p>
                                </motion.div>

                                {/* Sessions Count */}
                                <motion.div
                                    className="bg-white rounded-xl shadow p-6"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.3, delay: 0.1 }}
                                >
                                    <h3 className="font-medium text-gray-700 mb-2">Total Sessions</h3>
                                    <div className="text-3xl font-bold text-purple-600">
                                        {sessions.length}
                                    </div>
                                    <p className="text-sm text-gray-500 mt-2">
                                        Last session: {timelineData.length > 0 ?
                                            format(timelineData[timelineData.length - 1].date, 'MMM d, yyyy') : 'N/A'}
                                    </p>
                                </motion.div>

                                {/* Most Improved Skill */}
                                <motion.div
                                    className="bg-white rounded-xl shadow p-6"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.3, delay: 0.2 }}
                                >
                                    <h3 className="font-medium text-gray-700 mb-2">Most Improved Skill</h3>
                                    <div className="flex items-center">
                                        <div className="text-xl font-bold text-green-600 mr-2">
                                            {mostImprovedSkill || "N/A"}
                                        </div>
                                        {mostImprovedSkill && <FaArrowUp className="text-green-500" />}
                                    </div>
                                    <p className="text-sm text-gray-500 mt-2">
                                        Keep focusing on your strengths
                                    </p>
                                </motion.div>

                                {/* Areas Needing Focus */}
                                <motion.div
                                    className="bg-white rounded-xl shadow p-6"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.3, delay: 0.3 }}
                                >
                                    <h3 className="font-medium text-gray-700 mb-2">Areas Needing Focus</h3>
                                    <div className="text-xl font-medium text-amber-600">
                                        {improvements.length > 0 ? improvements[0].text.split(' ').slice(0, 3).join(' ') + '...' : "N/A"}
                                    </div>
                                    <p className="text-sm text-gray-500 mt-2">
                                        Most frequent improvement area
                                    </p>
                                </motion.div>
                            </div>

                            {/* Timeline Visualization */}
                            <div className="bg-white rounded-xl shadow p-6">
                                <h3 className="font-medium text-gray-700 mb-4">Performance Timeline</h3>

                                {timelineData.length > 1 ? (
                                    <div className="relative pt-4">
                                        <div className="absolute left-0 top-0 h-full flex items-center">
                                            <div className="h-full w-0.5 bg-gray-200"></div>
                                        </div>

                                        <div className="ml-8">
                                            {timelineData.map((point, index) => (
                                                <motion.div
                                                    key={index}
                                                    className="relative mb-8 last:mb-0"
                                                    initial={{ opacity: 0, x: -20 }}
                                                    animate={{ opacity: 1, x: 0 }}
                                                    transition={{ duration: 0.3, delay: index * 0.1 }}
                                                >
                                                    <div className="absolute -left-10 mt-1.5">
                                                        <div className={`flex items-center justify-center w-6 h-6 rounded-full ${index === timelineData.length - 1
                                                                ? 'bg-green-500'
                                                                : index === 0
                                                                    ? 'bg-blue-500'
                                                                    : 'bg-gray-400'
                                                            }`}>
                                                            {index === timelineData.length - 1 ? (
                                                                <FaTrophy className="text-white text-xs" />
                                                            ) : (
                                                                <div className="w-2 h-2 bg-white rounded-full"></div>
                                                            )}
                                                        </div>
                                                    </div>

                                                    <div className="flex flex-col md:flex-row md:items-center">
                                                        <div className="text-sm text-gray-500 min-w-[100px]">
                                                            {format(point.date, 'MMM d, yyyy')}
                                                        </div>

                                                        <div className="bg-gray-100 rounded-lg p-3 mt-2 md:mt-0 md:ml-4 flex-grow">
                                                            <div className="flex justify-between items-center mb-1">
                                                                <h4 className="font-medium text-gray-800">{point.title}</h4>
                                                                <span className="text-sm bg-gray-200 px-2 py-0.5 rounded">
                                                                    {point.interviewType}
                                                                </span>
                                                            </div>

                                                            <div className="flex items-center mt-2">
                                                                <span className="text-sm text-gray-600 mr-2">Score:</span>
                                                                <div className="flex-grow bg-gray-200 h-2 rounded-full">
                                                                    <div
                                                                        className="h-full rounded-full bg-blue-500"
                                                                        style={{ width: `${point.score}%` }}
                                                                    ></div>
                                                                </div>
                                                                <span className="ml-2 text-sm font-medium">{point.score}%</span>
                                                            </div>

                                                            {index > 0 && (
                                                                <div className="mt-2 text-sm">
                                                                    <span className={
                                                                        point.score > timelineData[index - 1].score
                                                                            ? 'text-green-500'
                                                                            : point.score < timelineData[index - 1].score
                                                                                ? 'text-red-500'
                                                                                : 'text-gray-500'
                                                                    }>
                                                                        {point.score > timelineData[index - 1].score
                                                                            ? `↑ Improved by ${point.score - timelineData[index - 1].score}%`
                                                                            : point.score < timelineData[index - 1].score
                                                                                ? `↓ Decreased by ${timelineData[index - 1].score - point.score}%`
                                                                                : 'No change'
                                                                        }
                                                                    </span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </motion.div>
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-center py-4 text-gray-500">
                                        Complete more sessions to see your progress over time
                                    </div>
                                )}
                            </div>

                            {/* Skill Progress Cards */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                                {skillProgress.map((skill, index) => {
                                    const progress = calculateSkillProgress(skill.data);
                                    return (
                                        <motion.div
                                            key={index}
                                            className="bg-white rounded-xl shadow p-6"
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.3, delay: index * 0.1 }}
                                        >
                                            <h3 className="font-medium text-gray-700 mb-3">{skill.name} Skill</h3>

                                            {skill.data.length > 1 ? (
                                                <>
                                                    <div className="flex items-center mb-4">
                                                        <div className="text-2xl font-bold mr-2">
                                                            {Math.round(skill.data[skill.data.length - 1].value)}%
                                                        </div>
                                                        <span className={`text-sm font-medium ${progress.improved ? 'text-green-500' : 'text-red-500'
                                                            }`}>
                                                            {progress.improved ? '↑' : '↓'}
                                                            {Math.round(progress.value)}%
                                                        </span>
                                                    </div>

                                                    <div className="relative h-12">
                                                        {skill.data.map((point, pointIndex) => (
                                                            <div
                                                                key={pointIndex}
                                                                className="absolute bottom-0 bg-blue-500 rounded-t w-2"
                                                                style={{
                                                                    height: `${point.value}%`,
                                                                    left: `${(pointIndex / (skill.data.length - 1)) * 100}%`,
                                                                    opacity: pointIndex === skill.data.length - 1 ? 1 : 0.7
                                                                }}
                                                            ></div>
                                                        ))}
                                                    </div>

                                                    <div className="flex justify-between mt-1 text-xs text-gray-500">
                                                        <span>{format(skill.data[0].date, 'MMM d')}</span>
                                                        <span>{format(skill.data[skill.data.length - 1].date, 'MMM d')}</span>
                                                    </div>
                                                </>
                                            ) : (
                                                <div className="text-center py-4 text-gray-500">
                                                    Not enough data
                                                </div>
                                            )}
                                        </motion.div>
                                    );
                                })}
                            </div>
                        </section>
                    )}

                    {/* Top Improvements Summary */}
                    <section className="mb-10">
                        <h2 className="text-xl font-semibold text-gray-700 mb-4">Top Areas for Improvement</h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {topImprovements.map((improvement, index) => (
                                <motion.div
                                    key={index}
                                    className="bg-white rounded-xl shadow p-6"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.3, delay: index * 0.1 }}
                                >
                                    <div className="flex items-center gap-3 mb-3">
                                        <div className="bg-blue-100 text-blue-800 rounded-full w-8 h-8 flex items-center justify-center font-bold">
                                            {index + 1}
                                        </div>
                                        <h3 className="font-medium text-gray-800">Improvement Area</h3>
                                    </div>
                                    <p className="text-gray-600 mb-3">{improvement.text}</p>
                                    <div className="text-sm text-gray-500">
                                        Mentioned in {improvement.count} {improvement.count === 1 ? 'session' : 'sessions'}
                                    </div>
                                </motion.div>
                            ))}

                            {topImprovements.length === 0 && (
                                <div className="col-span-3 bg-gray-50 rounded-xl p-6 text-center">
                                    <p className="text-gray-600">No improvement suggestions available yet.</p>
                                </div>
                            )}
                        </div>
                    </section>

                    {/* Sessions List */}
                    <section>
                        <div className="flex flex-col md:flex-row justify-between items-center mb-6">
                            <h2 className="text-xl font-semibold text-gray-700 mb-4 md:mb-0">Your Interview Sessions</h2>

                            <div className="flex flex-col md:flex-row gap-4 w-full md:w-auto">
                                <div className="relative">
                                    <input
                                        type="text"
                                        placeholder="Search sessions..."
                                        className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                    <FaSearch className="absolute left-3 top-3 text-gray-400" />
                                </div>

                                <button
                                    className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
                                    onClick={() => setSortBy(sortBy === "date" ? "score" : "date")}
                                >
                                    <FaSort />
                                    Sort by: {sortBy === "date" ? "Date" : "Score"}
                                </button>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 gap-4">
                            {sortedSessions.map((session, index) => (
                                <motion.div
                                    key={session.id}
                                    className="bg-white rounded-xl shadow hover:shadow-md transition p-6"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.3, delay: index * 0.05 }}
                                >
                                    <div className="flex flex-col md:flex-row justify-between">
                                        <div>
                                            <h3 className="font-semibold text-lg text-gray-800 mb-2">{session.title}</h3>
                                            <div className="flex items-center gap-2 text-gray-500 mb-4">
                                                <FaCalendarAlt />
                                                <span>{formatDistanceToNow(session.date, { addSuffix: true })}</span>
                                                <span className="bg-gray-200 px-2 py-1 rounded text-xs">
                                                    {session.interviewType}
                                                </span>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-2 mb-4 md:mb-0">
                                            <div className="flex items-center gap-2">
                                                <FaChartBar className="text-blue-500" />
                                                <span className="font-medium">Score:</span>
                                            </div>
                                            <div className="text-lg font-bold text-blue-600">
                                                {session.score}%
                                            </div>
                                        </div>
                                    </div>

                                    {session.improvements.length > 0 && (
                                        <div>
                                            <h4 className="font-medium text-gray-700 mb-2">Areas for Improvement:</h4>
                                            <ul className="pl-6 list-disc text-gray-600">
                                                {session.improvements.slice(0, 3).map((improvement, i) => (
                                                    <li key={i} className="mb-1">{improvement}</li>
                                                ))}
                                                {session.improvements.length > 3 && (
                                                    <li className="text-blue-500">
                                                        + {session.improvements.length - 3} more
                                                    </li>
                                                )}
                                            </ul>
                                        </div>
                                    )}
                                </motion.div>
                            ))}

                            {sortedSessions.length === 0 && (
                                <div className="bg-gray-50 rounded-xl p-8 text-center">
                                    <p className="text-gray-600">No sessions match your search.</p>
                                </div>
                            )}
                        </div>
                    </section>
                </>
            )}
        </div>
    );
}

// Types
interface ImprovementItem {
    text: string;
    count: number;
    sessions: string[];
}

interface SessionDetail {
    id: string;
    title: string;
    date: Date;
    interviewType: string;
    score: number;
    questions: Question[];
    improvements: string[];
    skillScores: {
        behavioral: number;
        problemSolving: number;
        technicalDepth: number;
        communication: number;
    }
}

interface SkillProgressData {
    name: string;
    data: { date: Date, value: number }[];
}

interface TimelinePoint {
    date: Date;
    score: number;
    title: string;
    interviewType: string;
}